// AXIS ZERO - HUD Component
// Minimal esport style: Trophy+BEST top-left, TIME center, STREAK below, sound top-right

import React from 'react';
import { Volume2, VolumeX, Trophy } from 'lucide-react';
import { GAME_STATES } from '../../game';

export const HUD = ({ 
  gameState, 
  score, 
  highScore, 
  streak = 0,
  isHighStreak = false,
  isMuted, 
  onToggleSound,
}) => {
  const formatScore = (time) => {
    if (time === null || time === undefined) return '0.00';
    return time.toFixed(2);
  };

  if (gameState === GAME_STATES.GAME_OVER || gameState === GAME_STATES.READY) {
    return null;
  }

  const mainColor = isHighStreak ? '#FF1744' : '#00E5FF';
  const glowColor = isHighStreak ? 'rgba(255, 23, 68, 0.8)' : 'rgba(0, 229, 255, 0.8)';
  
  // Check if current score exceeds high score during gameplay
  const isCurrentlyBest = highScore !== null && highScore > 0 && score > highScore;

  return (
    <div 
      data-testid="game-hud"
      className="absolute inset-0 pointer-events-none z-10"
    >
      {/* Top Left: Trophy + BEST */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5">
        <Trophy 
          size={14} 
          style={{ 
            color: mainColor, 
            opacity: 0.7,
            filter: `drop-shadow(0 0 4px ${glowColor})`
          }} 
        />
        <span 
          data-testid="hud-best-score"
          className="text-xs font-semibold uppercase tracking-wider"
          style={{ 
            color: mainColor,
            opacity: 0.7,
            textShadow: `0 0 6px ${glowColor}`,
            fontFamily: '"Orbitron", sans-serif',
          }}
        >
          BEST {highScore !== null && highScore > 0 ? formatScore(highScore) : '\u2014'}
        </span>
      </div>

      {/* Top Right: Sound Toggle + NEW BEST badge */}
      <div className="absolute top-3 right-3 flex items-center gap-2">
        {isCurrentlyBest && (
          <span 
            data-testid="new-best-badge"
            className="text-xs font-bold uppercase tracking-wider animate-pulse"
            style={{ 
              color: '#FF1744',
              textShadow: '0 0 8px rgba(255, 23, 68, 0.8)',
              fontFamily: '"Orbitron", sans-serif',
            }}
          >
            NEW BEST!
          </span>
        )}
        <button
          data-testid="hud-sound-toggle"
          onClick={onToggleSound}
          className="pointer-events-auto p-1 opacity-50 hover:opacity-100 transition-opacity"
          style={{ color: isMuted ? '#333' : mainColor }}
        >
          {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      </div>

      {/* Top Center: TIME + STREAK */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-center">
        {/* Main Time */}
        <div 
          data-testid="current-score"
          className="text-4xl md:text-5xl lg:text-6xl font-black tracking-wider"
          style={{ 
            color: mainColor,
            textShadow: `0 0 20px ${glowColor}, 0 0 40px ${glowColor}`,
            fontFamily: '"Orbitron", sans-serif',
            lineHeight: 1,
          }}
        >
          {formatScore(score)}
        </div>
        
        {/* Streak with horizontal lines */}
        {streak > 0 && (
          <div 
            data-testid="streak-display"
            className="flex items-center justify-center gap-2 mt-2"
          >
            <div 
              className="h-px flex-1 max-w-[30px]"
              style={{ backgroundColor: mainColor, opacity: 0.4 }}
            />
            <span
              className="text-sm font-bold uppercase tracking-[0.2em]"
              style={{ 
                color: mainColor,
                opacity: 0.8,
                textShadow: `0 0 8px ${glowColor}`,
                fontFamily: '"Rajdhani", sans-serif',
              }}
            >
              x{streak} STREAK
            </span>
            <div 
              className="h-px flex-1 max-w-[30px]"
              style={{ backgroundColor: mainColor, opacity: 0.4 }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default HUD;
